/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getDatabase,
  onValue,
  orderByChild,
  query,
  ref,
  remove,
  update,
} from "firebase/database";
import { useAuth } from "@/contexts/AuthContext";
import type { SafeCircleNotification } from "@/types/safecircle";

export function useSafeCircleNotifications() {
  const { user } = useAuth();
  const [inbox, setInbox] = useState<SafeCircleNotification[]>([]);
  const [latestFcm, setLatestFcm] = useState<SafeCircleNotification | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setInbox([]);
      setLoading(false);
      return;
    }
    const db = getDatabase();
    const inboxQuery = query(
      ref(db, `safeCircleNotifications/${user.uid}`),
      orderByChild("createdAt"),
    );
    const unsub = onValue(inboxQuery, (snap) => {
      const data = snap.val();
      const list = data
        ? Object.entries(data).map(
            ([id, val]) => ({
              ...(val as SafeCircleNotification),
              id,
            }),
          )
        : [];
      setInbox(list.sort((a, b) => b.createdAt - a.createdAt));
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  // Surface foreground FCM messages as in-app toasts
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;
      const payload = detail?.notification || detail?.data || {};
      const type = detail?.data?.type || "SOS_TRIGGERED";
      const notif: SafeCircleNotification = {
        id: `fcm_${Date.now()}`,
        type,
        toUid: user?.uid ?? "",
        title: payload?.title ?? "Notification",
        body: payload?.body ?? "",
        data: detail?.data,
        createdAt: Date.now(),
      };
      setLatestFcm(notif);
    };
    window.addEventListener("fcm-message", handler as EventListener);
    return () =>
      window.removeEventListener("fcm-message", handler as EventListener);
  }, [user]);

  const markRead = useCallback(
    (notifId: string) => {
      if (!user) return;
      const db = getDatabase();
      update(ref(db, `safeCircleNotifications/${user.uid}/${notifId}`), {
        readAt: Date.now(),
      });
    },
    [user],
  );

  const dismiss = useCallback(
    (notifId: string) => {
      if (!user) return;
      const db = getDatabase();
      remove(ref(db, `safeCircleNotifications/${user.uid}/${notifId}`));
    },
    [user],
  );

  const markAllRead = useCallback(() => {
    if (!user) return;
    const db = getDatabase();
    const now = Date.now();
    for (const n of inbox) {
      if (!n.readAt) {
        update(ref(db, `safeCircleNotifications/${user.uid}/${n.id}`), {
          readAt: now,
        });
      }
    }
  }, [user, inbox]);

  const unreadCount = useMemo(
    () => inbox.filter((n) => !n.readAt).length,
    [inbox],
  );

  return {
    inbox,
    unreadCount,
    loading,
    latestFcm,
    markRead,
    markAllRead,
    dismiss,
  };
}
